import * as THREE from 'three';
import { PlanogramObject,Anchor } from "./PlanogramObject.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramRendererResources, PlanogramSubcomponentAttributes, PlanogramObjectAttributes } from './Resources.js';
import { PlanogramImage } from './PlanogramImage.js';
import { PlanogramComponent } from './PlanogramComponent.js';
import { PlanogramDivider } from './PlanogramDivider.js';
import { PlanogramPeg } from './PlanogramPeg.js';

export class PlanogramSubcomponent extends HTMLElement {

    //#region Attributes
    static CommonAttributes = PlanogramSubcomponentAttributes.CommonAttributes;
    static BackBoardAttributes = PlanogramSubcomponentAttributes.BackBoardAttributes;
    static PegBoardClipStripSlotwallAttributes = PlanogramSubcomponentAttributes.PegBoardClipStripSlotwallAttributes;
    static ShelfAttributes = PlanogramSubcomponentAttributes.ShelfAttributes
    static ChestAttributes = PlanogramSubcomponentAttributes.ChestAttributes;
    //#endregion

    static get observedAttributes(){
        return [
            ...Object.values(PlanogramObjectAttributes.Attributes),
            ...Object.values(PlanogramSubcomponent.CommonAttributes),
            ...Object.values(PlanogramSubcomponent.BackBoardAttributes),
            ...Object.values(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes),
            ...Object.values(PlanogramSubcomponent.ShelfAttributes),
            ...Object.values(PlanogramSubcomponent.ChestAttributes) 
        ];
    }

    //#region Public Properties

    get group (){ return this.#PlanogramComponent?.group; }

    get totalMeshCount(){
        var count = 1;
        if(this.#PlanogramComponent.type === PlanogramComponent.Types.Chest && !this.#hideChestWalls) count += 4;
        if(this.#PlanogramComponent.type === PlanogramComponent.Types.Shelf && this.#hasRisers) 
            count += [this.showFront, this.showBack, this.showLeft, this.showRight].reduce((a,c)=> a + c >> 0 , 0);
        count += this.#dividers.length;
        if(this.hasPegHoles && this.#pegs.length > 0){
            for(var peg of this.#pegs){
                count ++;
                if(peg.drawTag)count++;
                if(peg.drawUpper)count++;
            }
        }
        return count;
    }

    get nonRotatedBoundingBox(){
        if(this.sizes.length === this.positions.length){
            return this.sizes.reduce((a,c,i)=>{ 
                const center = this.positions[i];
                const size = new THREE.Vector3(c.x, c.y, c.z);
                const box = new THREE.Box3().setFromCenterAndSize(center, size);
                return a.union(box);
            }, new THREE.Box3());
        }
        return new THREE.Box3();
    }

    get groupBoundingBox(){
        if(this.sizes.length === this.positions.length){
            const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
                this.rotationX, this.rotationY, this.rotationZ, 'XYZ'));
            
            return this.sizes.reduce((a,c,i)=>{
                const center = this.positions[i];
                const halfSize = new THREE.Vector3(c.x / 2, c.y / 2, c.z / 2);
                const corners = [
                    new THREE.Vector3(-halfSize.x, -halfSize.y, -halfSize.z),
                    new THREE.Vector3(-halfSize.x, -halfSize.y,  halfSize.z),
                    new THREE.Vector3(-halfSize.x,  halfSize.y, -halfSize.z),
                    new THREE.Vector3(-halfSize.x,  halfSize.y,  halfSize.z),
                    new THREE.Vector3( halfSize.x, -halfSize.y, -halfSize.z),
                    new THREE.Vector3( halfSize.x, -halfSize.y,  halfSize.z),
                    new THREE.Vector3( halfSize.x,  halfSize.y, -halfSize.z),
                    new THREE.Vector3( halfSize.x,  halfSize.y,  halfSize.z)
                ];
                const transformedCorners = corners.map(corner => {
                    return corner.applyMatrix4(rotationMatrix).add(center);
                });
                const rotatedBox = new THREE.Box3();
                transformedCorners.forEach(corner => rotatedBox.expandByPoint(corner));
                
                return a.union(rotatedBox);
            }, new THREE.Box3());
        }
        return new THREE.Box3();
    }

    firstRender = true;
    //#region Geometry Attribute Properties

    ///new attribute setup
    /// sizes = vec4( width, height, depth, imageDepth);
    /// frontBackFill = vec4( frontColour, frontStyle, backColour, backStyle);
    /// topBottomFill = vec4( topColour, topStyle, bottomColour, bottomStyle);
    /// leftRightFill = vec4( leftColour, leftStyle, rightColour, rightStyle);
    /// options = vec4( lineColour, lineThickness, type, transparency);
    /// others remaining the same
    
    /** @returns {THREE.Vector4[]} */
    get sizes(){
        const imageDepth = this.#image?.depth ?? -1;
        const sizes = [ new THREE.Vector4(this.width, this.#isChest ? this.faceThicknessBottom : this.height, this.depth, imageDepth) ];
        if(this.#isChest && !this.#hideChestWalls){
            var sideOffset = (this.depth - this.faceThicknessFront - this.faceThicknessBack) / this.depth;
            sizes.push(
                new THREE.Vector4(this.faceThicknessLeft,this.height - this.faceThicknessBottom, this.depth * sideOffset, imageDepth),
                new THREE.Vector4(this.faceThicknessRight,this.height - this.faceThicknessBottom, this.depth * sideOffset, imageDepth),
                new THREE.Vector4(this.width,this.height - this.faceThicknessBottom, this.faceThicknessFront, imageDepth),
                new THREE.Vector4(this.width,this.height - this.faceThicknessBottom, this.faceThicknessBack, imageDepth),
            );
        }
        if(this.#hasRisers){
            if(this.showFront) sizes.push(new THREE.Vector4(this.width, this.riserHeight, this.riserThickness, -1));
            if(this.showBack) sizes.push(new THREE.Vector4(this.width, this.riserHeight, this.riserThickness, -1));
            if(this.showLeft) sizes.push(new THREE.Vector4(this.riserThickness, this.riserHeight, this.depth, -1));
            if(this.showRight) sizes.push(new THREE.Vector4(this.riserThickness, this.riserHeight, this.depth, -1));
        }
        if(this.hasDividers)
            sizes.push(...this.#dividers.map(divider => new THREE.Vector4(divider.width, divider.height, divider.depth, -1)));
        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                sizes.push(new THREE.Vector4(this.merchConstraintRow1Width, this.merchConstraintRow1Height, peg.depth, -1));
                if(peg.drawTag) sizes.push(new THREE.Vector4(peg.tagWidth, peg.tagHeight, 0.3, -1));
                if(peg.drawUpper) sizes.push(new THREE.Vector4(this.merchConstraintRow1Width, this.merchConstraintRow1Height, peg.depth, -1));
            }
        }
        return sizes;
    }

    /** @returns {THREE.Vector4[][]} */
    get colours(){
        const frontBackFill = [];
        const topBottomFill = [];
        const leftRightFill = [];
        /// The first object is a chest wall even though it's the original subcomponent
        if(this.#isChest && !this.#hideChestWalls){
            this.#setSingleColourAndStyle(this.bottomColour, this.bottomStyle, frontBackFill, topBottomFill, leftRightFill);
            this.#setSingleColourAndStyle(this.rightColour, this.rightStyle, frontBackFill, topBottomFill, leftRightFill);
            this.#setSingleColourAndStyle(this.leftColour, this.leftStyle, frontBackFill, topBottomFill, leftRightFill);
            this.#setSingleColourAndStyle(this.frontColour, this.frontStyle, frontBackFill, topBottomFill, leftRightFill);
            this.#setSingleColourAndStyle(this.backColour, this.backStyle, frontBackFill, topBottomFill, leftRightFill);
        }else{
            frontBackFill.push( new THREE.Vector4(this.frontColour, this.frontStyle, this.backColour, this.backStyle) );
            topBottomFill.push( new THREE.Vector4(this.topColour, this.topStyle, this.bottomColour, this.bottomStyle) );
            leftRightFill.push( new THREE.Vector4(this.leftColour, this.leftStyle, this.rightColour, this.rightStyle) );
        }
        if(this.#hasRisers){
            if(this.showFront) this.#setSingleColourAndStyle(this.riserColour, this.riserFillStyle, frontBackFill, topBottomFill, leftRightFill);
            if(this.showBack) this.#setSingleColourAndStyle(this.riserColour, this.riserFillStyle, frontBackFill, topBottomFill, leftRightFill);
            if(this.showLeft) this.#setSingleColourAndStyle(this.riserColour, this.riserFillStyle, frontBackFill, topBottomFill, leftRightFill);
            if(this.showRight) this.#setSingleColourAndStyle(this.riserColour, this.riserFillStyle, frontBackFill, topBottomFill, leftRightFill);
        }
        if(this.hasDividers){
            this.#dividers.forEach(divider => 
                this.#setSingleColourAndStyle(divider.colour, divider.fillStyle, frontBackFill, topBottomFill, leftRightFill));
        }
        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                this.#setSingleColourAndStyle(0x585858, 0, frontBackFill, topBottomFill, leftRightFill);
                if(peg.drawTag) this.#setSingleColourAndStyle(0xd2b48c, 0, frontBackFill, topBottomFill, leftRightFill);
                if(peg.drawUpper) this.#setSingleColourAndStyle(0x585858, 0, frontBackFill, topBottomFill, leftRightFill);
            }
        }
        return [ frontBackFill, topBottomFill, leftRightFill ];
    }

    /** @returns {THREE.Vector4[]} */
    get options(){
        const options = [ new THREE.Vector4(this.lineThickness, this.lineColour,  this.#PlanogramComponent.type, this.transparency) ];
        if(this.#isChest && !this.#hideChestWalls){
            var chestTransparency = PlanogramRenderer.get(this).chestWallTransparency;
            var transparency = chestTransparency > 0 ? this.transparency * chestTransparency : 0;
            options.splice(0,options.length,...[ 
                new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Chest_Wall, 1),
                new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Chest_Wall, transparency),
                new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Chest_Wall, transparency),
                new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Chest_Wall, transparency),
                new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Chest_Wall, transparency)
             ]).flat();
        }

        if(this.#hasRisers){
            if(this.showFront) options.push( new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Shelf_Riser, this.riserTransparency) );
            if(this.showBack) options.push( new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Shelf_Riser, this.riserTransparency) );
            if(this.showLeft) options.push( new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Shelf_Riser, this.riserTransparency) );
            if(this.showRight) options.push( new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Shelf_Riser, this.riserTransparency) );
        }
        if(this.hasDividers)
            options.push(...this.#dividers.map(divider => new THREE.Vector4(this.lineThickness, this.lineColour, PlanogramComponent.Types.Divider, 1)));
        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                options.push( new THREE.Vector4(0.1, this.lineColour, PlanogramComponent.Types.PegProng, 1) );
                if(peg.drawTag) options.push( new THREE.Vector4(0.1, this.lineColour, PlanogramComponent.Types.PegTag, 1 ) );
                if(peg.drawUpper) options.push( new THREE.Vector4(0.1, this.lineColour, PlanogramComponent.Types.PegProng, 1) );
            }
        }
        return options;
    }

    /** @returns {THREE.Vector4[][]} */
    get drawArgs(){
        const drawArgs1 = Array(this.totalMeshCount).fill( 
            this.#hasNotches ? new THREE.Vector4(this.notchStartX, this.notchStartY, this.notchWidth, this.notchHeight) : 
            this.hasPegHoles ? new THREE.Vector4(this.merchConstraintRow1Width, this.merchConstraintRow1Height, 
                this.merchConstraintRow2Width, this.merchConstraintRow2Height) : new THREE.Vector4());
        const drawArgs2 = Array(this.totalMeshCount).fill( 
            this.#hasNotches ? new THREE.Vector4(this.notchSpacingX, this.notchSpacingY, this.notchType, 0) : 
            this.hasPegHoles ? new THREE.Vector4(this.merchConstraintRow1StartX, this.merchConstraintRow1StartY, 
                this.merchConstraintRow2StartX, this.merchConstraintRow2StartY) : new THREE.Vector4());
        const drawArgs3 = Array(this.totalMeshCount).fill( this.hasPegHoles ? new THREE.Vector4(this.merchConstraintRow1SpacingX, this.merchConstraintRow1SpacingY, 
            this.merchConstraintRow2SpacingX, this.merchConstraintRow2SpacingY) : new THREE.Vector4());
        return [ drawArgs1, drawArgs2, drawArgs3 ];
    }

    /** @returns {THREE.Vector3[]} */
    get positions(){
        const positions = [];
        const defaultMatrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
            this.rotationX, this.rotationY, this.rotationZ, 'XYZ')).multiply(new THREE.Matrix4().makeTranslation(this.x, this.y, this.z));

        if(this.#isChest && !this.#hideChestWalls ){
            const bottom = this.faceThicknessBottom /2 - this.height/2;
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().makeTranslation( 0, bottom, 0 ))));
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                makeTranslation( this.width/2 - this.faceThicknessRight/2, bottom + this.height/2, this.faceThicknessBack/2 - this.faceThicknessFront/2  ))));
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                makeTranslation( -this.width/2 + this.faceThicknessLeft/2, bottom + this.height/2, this.faceThicknessBack/2 - this.faceThicknessFront/2  ))));
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                makeTranslation( 0, bottom + this.height/2, this.depth/2 - this.faceThicknessFront/2 ))));
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                makeTranslation( 0, bottom + this.height/2, -this.depth/2 + this.faceThicknessBack/2 ))));
        }else {
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().
                multiply(new THREE.Matrix4().makeTranslation(0, this.#isChest ? this.faceThicknessBottom /2 - this.height/2 : 0, 0))));
        }

        if( this.#isShelf && this.totalMeshCount > 1 ){
            if(this.showFront)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 0,this.height/2 + this.riserHeight/2,this.depth/2 + this.riserThickness/2 ))));
            if(this.showBack)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 0,this.height/2 + this.riserHeight/2, -this.depth/2 - this.riserThickness/2 ))));
        if(this.showLeft)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( -this.width/2 - this.riserThickness/2,this.height/2 + this.riserHeight/2,0 ))));
            if(this.showRight)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( this.width/2 + this.riserThickness/2,this.height/2 + this.riserHeight/2,0 ))));
        }

        if(this.hasDividers){
            for(var divider of this.#dividers){
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 
                        divider.x - this.width/2 + divider.width/2, 
                        divider.y - this.height/2 + divider.height/2, 
                       divider.z - this.depth/2 + divider.depth/2 ))));
            }
        }

        if(this.hasPegHoles && this.#pegs.length > 0){
            for(var peg of this.#pegs){
                const pegX = peg.x - this.width/2;
                const pegY = peg.y - this.height/2;
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( pegX, pegY, this.depth/2 + peg.depth/2 ))));
                if(peg.drawTag) positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( pegX, pegY + peg.tagYOffset - peg.tagHeight/2, this.depth/2 + peg.depth ))));
                if(peg.drawUpper){
                    const pegTagTop = pegY + peg.tagYOffset - peg.tagHeight/2;
                    positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                        multiply(new THREE.Matrix4().makeTranslation(pegX, pegTagTop + (peg.tagHeight/2 - this.merchConstraintRow1Height/2), this.depth/2 + peg.depth/2 ))));
                }
            }
        }

        return positions;
    }

    //#endregion

    //#region Common Attributes
    get width() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Width)) || 10; }
    get height() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Height)) || 10; }
    get depth() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Depth)) || 10; }

    get x() { return Number(this.getAttribute(PlanogramObject.Attributes.X)) || 0;}
    get y() { return Number(this.getAttribute(PlanogramObject.Attributes.Y)) || 0;}
    get z() { return Number(this.getAttribute(PlanogramObject.Attributes.Z)) || 0;}

    get rotationX() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationX)) || 0;}
    get rotationY() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationY)) || 0;}
    get rotationZ() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationZ)) || 0;}

    get frontColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.FrontColour), 0xffffff); }
    get backColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.BackColour), this.frontColour); }
    get leftColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.LeftColour), this.frontColour); }
    get rightColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.RightColour), this.frontColour); }
    get topColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.TopColour), this.frontColour); }
    get bottomColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.CommonAttributes.BottomColour), this.frontColour); }

    get frontStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.FrontStyle, 0);}
    get backStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.BackStyle, this.frontStyle);}
    get leftStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.LeftStyle, this.frontStyle);}
    get rightStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.RightStyle, this.frontStyle);}
    get topStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.TopStyle, this.frontStyle);}
    get bottomStyle(){return this.#computeStyle(PlanogramSubcomponent.CommonAttributes.BottomStyle, this.frontStyle);}

    get lineThickness() { return 0.25;}
    get lineColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramObject.Attributes.LineColour), 0x000000);}
    get transparency() { return Number(this.getAttribute(PlanogramObject.Attributes.Transparency)) || 1;}
    get selected() { return this.hasAttribute(PlanogramObject.Attributes.Selected); }
    get snapAnchor(){  return this.getAttribute(PlanogramSubcomponent.CommonAttributes.SnapAnchor); }
    //#endregion

    //#region Backboard Attributes
    get notchType() {
        var value = Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchType));
        return  this.hasAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchType) && !isNaN(value) ? value : 1;
    }
    get showNotches() { return this.hasAttribute(PlanogramSubcomponent.BackBoardAttributes.ShowNotches); }
    get notchStartX() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchStartX)) || 0; }
    get notchStartY() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchStartY)) || 15; }
    get notchSpacingX() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchSpacingX)) || 0; }
    get notchSpacingY() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchSpacingY)) || 5; }
    get notchWidth() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchWidth)) || 1; }
    get notchHeight() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchHeight)) || 3; }
    get notchYOffset() { return Number(this.getAttribute(PlanogramSubcomponent.BackBoardAttributes.NotchYOffset)) || 0; }
    //#endregion

    //#region Shelf Attributes
    get riserColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserColour), 0xffffff); }
    get riserHeight() { return Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserHeight)) || 5; }
    get riserThickness() { return Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserThickness)) || 1; }
    get riserFillStyle() { return this.#computeStyle(PlanogramSubcomponent.ShelfAttributes.RiserFillStyle, 0); }
    get riserTransparency() {
        if(!this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.RiserTransparency))return 1;
        var value = Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserTransparency)); 
        return isNaN(value) ? 1 : value; 
    }
    get showBack() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowBack); }
    get showFront() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowFront); }
    get showLeft() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowLeft); }
    get showRight() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowRight); }
    //#endregion

    //#region PegBoard/ClipStrip/Slotwall Attributes
    get merchConstraintRow1Width() { return this.#PlanogramComponent.type === PlanogramComponent.Types.SlotWall? 1: 
        ( Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1Width)) || 1 ); }
    get merchConstraintRow1Height() { return Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1Height)) || 1; }
    get merchConstraintRow2Width() { return this.#PlanogramComponent.type === PlanogramComponent.Types.Peg? 
        (Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2Width)) || 1) : 0; }
    get merchConstraintRow2Height() { return this.#PlanogramComponent.type === PlanogramComponent.Types.Peg?
        (Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2Height)) || 1): 0; }
    get merchConstraintRow1StartX() { return Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1StartX)) || 4; }
    get merchConstraintRow1StartY() { return this.#PlanogramComponent.type === PlanogramComponent.Types.SlotWall? 0: 
        ( Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1StartY)) || 4); }
    get merchConstraintRow2StartX() { return this.#PlanogramComponent.type === PlanogramComponent.Types.Peg?
        (Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2StartX)) || 0): 0; }
    get merchConstraintRow2StartY() { return this.#PlanogramComponent.type === PlanogramComponent.Types.Peg?
        (Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2StartY)) || 0): 0; }
    get merchConstraintRow1SpacingX() { return this.#PlanogramComponent.type === PlanogramComponent.Types.SlotWall? 0: 
        (Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1SpacingX)) || 4); }
    get merchConstraintRow1SpacingY() { return Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow1SpacingY)) || 4; }
    get merchConstraintRow2SpacingX() { return Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2SpacingX)) || 0; }
    get merchConstraintRow2SpacingY() { return Number(this.getAttribute(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.MerchConstraintRow2SpacingY)) || 0; }
    //#endregion

    //#region Chest Attributes
    get faceThicknessFront() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessFront)) || 4; }
    get faceThicknessBack() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessBack)) || 4; }
    get faceThicknessLeft() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessLeft)) || 4; }
    get faceThicknessRight() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessRight)) || 4; }
    get faceThicknessTop() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessTop)) || 4; }
    get faceThicknessBottom() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessBottom)) || 4; }
    //#endregion

    //#endregion
    
    //#region Private Properties
    /**@type {PlanogramComponent} */
    #PlanogramComponent = null;
    /**@type {PlanogramImage} */
    get #image(){ return this.querySelector("planogram-image"); }
    get hasPegHoles (){return this.#PlanogramComponent.type === PlanogramComponent.Types.Peg || this.#PlanogramComponent.type === PlanogramComponent.Types.SlotWall || 
        this.#PlanogramComponent.type === PlanogramComponent.Types.ClipStrip || this.#PlanogramComponent.type === PlanogramComponent.Types.Bar || this.#PlanogramComponent.type === PlanogramComponent.Types.Custom;}
    get #hasNotches (){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Backboard && 
        this.showNotches && this.notchHeight > 0 && this.notchWidth > 0; }
    get #hasRisers (){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Shelf && 
        (this.showFront || this.showBack || this.showLeft|| this.showRight); }
    get #hideChestWalls(){ return PlanogramContainer.get(this)?.hideChestWalls; }
    get canHaveDividers(){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Shelf || this.#PlanogramComponent.type === PlanogramComponent.Types.Chest || this.#PlanogramComponent.type === PlanogramComponent.Types.Custom; }
    get hasDividers(){return this.canHaveDividers && this.#dividers.length > 0;}
    /**@type {PlanogramDivider[]} */
    get #dividers(){ return [...this.querySelectorAll("planogram-divider")].filter(x=> Reflect.has(x, "firstRender") && x.firstRender === false); }
    /**@type {PlanogramPeg[]} */
    get #pegs(){ return [...this.querySelectorAll("planogram-peg")].filter(x=>Reflect.has(x, "firstRender") && x.firstRender === false); }

    get #isChest (){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Chest }
    get #isShelf (){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Shelf }
     /**@type {Anchor[]} */
    #anchors = [];
    get snapAnchors(){ return this.#anchors; }
    //#endregion


    constructor() { super(); }

    //#region Public Methods
    /**
     * Updating this subcomponent's instances
     * @param {THREE.InstancedMesh} mesh 
     * @param {number} currentCount 
     */
    generateSubcomponent( mesh, currentCount ){
        if(!mesh || !mesh.isInstancedMesh || mesh.count <= 0)return;
        
        var meshCount = currentCount;
        if(this.sizes.length === this.positions.length){
            for(var i = 0; i < this.sizes.length; i++){
                const size = this.sizes[i];
                const position = this.positions[i];
                const matrix = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z).multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
                    this.rotationX, this.rotationY, this.rotationZ, 'XYZ')))
                    .multiply(new THREE.Matrix4().makeScale(size.x, size.y, size.z));
                mesh.setMatrixAt( meshCount++, matrix);
            }
        }

    }

    dividerPlug(){
        this.#PlanogramComponent?.subComponentPlug(true);
    }

    pegsPlug(){
        this.#PlanogramComponent?.subComponentPlug(true);
    }

    imagePlug(){
        this.#PlanogramComponent?.subComponentPlug();
    }

    //#endregion

    //#region Private Methods
    
    /**
     * 
     * @param {string} style 
     * @param {number} defaultStyle 
     * @returns {number}
     */
    #computeStyle(style, defaultStyle = 0){
        if(this.hasAttribute(style))return Number(this.getAttribute(style));
        return defaultStyle;
    }

    #setSingleColourAndStyle(colour, style, frontBackFill, topBottomFill, leftRightFill){
        frontBackFill.push(new THREE.Vector4(colour, style, colour, style));
        topBottomFill.push(new THREE.Vector4(colour, style, colour, style));
        leftRightFill.push(new THREE.Vector4(colour, style, colour, style));
    } 


    //#endregion

    //#region Event Raise Methods
    /**
     * 
     * @param {THREE.Vector3} oldPosition 
     * @param {THREE.Vector3} newPosition 
     * @param {DataTransfer} dataTransfer 
     */
    raiseDropEvent( oldPosition, newPosition, dataTransfer ){
        const renderer = PlanogramRenderer.get(this);
        const delta =  new THREE.Vector3().fromArray(newPosition.toArray().map(x=>Math.round(x)));
        const event = new CustomEvent( PlanogramObject.customEvents.ObjectDropped, { bubbles: true, cancelable: true, detail: { x:newPosition.x ,y:newPosition.y, z:newPosition.z } });
        
        if( this.dispatched( event ) && renderer.showMessages ){
            console.groupCollapsed("Element Dropped");
            console.log("Old Position:", oldPosition );
            console.log("New Position:", newPosition );
            console.log("Delta:", delta );
            console.log("Event: ", event);
            console.log("Target: ", this);
            console.groupEnd();
        }
        

    }

    raiseClickEvent( x, y, rightClick = false){
        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const clickEvent = new MouseEvent( "click", { bubbles: true, cancelable: true, view: window, clientX: rect.x + x, clientY: rect.y + y });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: function() {return rightClick ? 2 : 0;}});
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function() {return this;}).bind(this)});
        if( this.dispatchEvent(clickEvent) && renderer.showMessages ){
            console.groupCollapsed("Object Selected"+ (rightClick? " and Context Menu":""));
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }
    
    dispatched( event ){
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }
    //#endregion

    //#region Web Component Lifecycle

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.firstRender || oldValue === newValue ) return;

        const remeshableShelfAttributes =[
            PlanogramSubcomponent.ShelfAttributes.RiserHeight,
            PlanogramSubcomponent.ShelfAttributes.RiserThickness,
            PlanogramSubcomponent.ShelfAttributes.ShowBack,
            PlanogramSubcomponent.ShelfAttributes.ShowFront,
            PlanogramSubcomponent.ShelfAttributes.ShowLeft,
            PlanogramSubcomponent.ShelfAttributes.ShowRight,
        ];
        const remeshable = [
            PlanogramSubcomponent.CommonAttributes.Width, 
            PlanogramSubcomponent.CommonAttributes.Height, 
            PlanogramSubcomponent.CommonAttributes.Depth,
            ...Object.values(PlanogramSubcomponent.ChestAttributes),
            ...remeshableShelfAttributes,
            PlanogramObject.Attributes.X, 
            PlanogramObject.Attributes.Y, 
            PlanogramObject.Attributes.Z,
            PlanogramObject.Attributes.RotationX, 
            PlanogramObject.Attributes.RotationY, 
            PlanogramObject.Attributes.RotationZ
        ];
        if(name === PlanogramSubcomponent.CommonAttributes.SnapAnchor){
            this.#anchors = Anchor.fromString(newValue);
        }
        this.#PlanogramComponent?.subComponentPlug(remeshable.includes(name));
    }

    connectedCallback() {
        if(this.parentElement instanceof PlanogramComponent){
            this.firstRender = false;
            this.#PlanogramComponent = this.parentElement;
            this.#PlanogramComponent.subComponentPlug(true);
            this.#anchors = Anchor.fromString(this.snapAnchor);
        }
    }

    disconnectedCallback(){
        this.dispose();
    }

    dispose() {
        this.#PlanogramComponent?.subComponentPlug(true);
    }
    //#endregion
    
    
}